import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import { API_ENDPOINTS, getImageUrl } from '../config/api';

const CowDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [cow, setCow] = useState({
    id: id || '',
    code: id || '',
    breed: '',
    name: 'Memuat data...',
    weight: '-',
    price: '0',
    farm: '',
    category: '',
    type: '',
    position: '',
    image: '/sapi1.jpg',
    uniqueCode: id || ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch data produk dan timbangan dari API
  useEffect(() => {
    const fetchCowData = async () => {
      if (!id) {
        setError('ID produk tidak ditemukan');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch data produk
        const produkResponse = await apiClient.get(API_ENDPOINTS.PRODUK.PUBLIC_DETAIL(id));
        
        // Handle struktur response dari API
        // Response format: { status, message, data: { produk: {...} } } atau { status, message, data: {...} }
        let produkData = null;
        
        if (produkResponse.data) {
          if (produkResponse.data.produk) {
            produkData = produkResponse.data.produk;
          } else if (produkResponse.data.produks && Array.isArray(produkResponse.data.produks) && produkResponse.data.produks.length > 0) {
            produkData = produkResponse.data.produks[0];
          } else {
            // Jika data langsung object produk
            produkData = produkResponse.data;
          }
        } else {
          // Jika response langsung object produk
          produkData = produkResponse;
        }

        if (!produkData || !produkData.id) {
          throw new Error('Data produk tidak ditemukan');
        }

        // Fetch images untuk produk
        let imageUrl = '/sapi1.jpg'; // Default fallback image
        try {
          const imagesResponse = await apiClient.get(API_ENDPOINTS.PRODUK.PUBLIC_IMAGES(id));
          if (
            imagesResponse.data &&
            imagesResponse.data.images &&
            Array.isArray(imagesResponse.data.images) &&
            imagesResponse.data.images.length > 0
          ) {
            const firstImage = imagesResponse.data.images[0];
            if (firstImage.url) {
              imageUrl = getImageUrl(firstImage.url);
            }
          }
        } catch (imageError) {
          console.warn('Error fetching image:', imageError);
        }

        // Fetch data timbangan produk
        let timbanganData = null;
        let bobotAktual = produkData.target_bobot ? `${produkData.target_bobot}Kg` : '-';
        
        try {
          // Coba dengan path parameter dulu (/api/timbangan-produk/public/{produk_id})
          let timbanganResponse = null;
          try {
            timbanganResponse = await apiClient.get(API_ENDPOINTS.TIMBANGAN_PRODUK.PUBLIC_BY_PRODUK(id));
          } catch (pathError) {
            // Jika gagal dengan path parameter, coba dengan query parameter
            console.warn('Trying query parameter for timbangan...');
            timbanganResponse = await apiClient.get(API_ENDPOINTS.TIMBANGAN_PRODUK.PUBLIC_BY_PRODUK_QUERY(id));
          }
          
          // Handle berbagai format response
          let timbangans = [];
          if (timbanganResponse.data && timbanganResponse.data.timbangan_produks) {
            timbangans = timbanganResponse.data.timbangan_produks;
          } else if (timbanganResponse.data && Array.isArray(timbanganResponse.data)) {
            timbangans = timbanganResponse.data;
          } else if (Array.isArray(timbanganResponse)) {
            timbangans = timbanganResponse;
          } else if (timbanganResponse.data && timbanganResponse.data.timbangan_produk) {
            timbangans = [timbanganResponse.data.timbangan_produk];
          }
          
          if (timbangans.length > 0) {
            // Ambil timbangan terbaru (biasanya yang pertama atau yang terakhir)
            timbanganData = timbangans[timbangans.length - 1]; // Ambil yang terakhir (paling baru)
            if (timbanganData.bobot) {
              bobotAktual = `${timbanganData.bobot}Kg`;
            }
          }
        } catch (timbanganError) {
          console.warn('Error fetching timbangan:', timbanganError);
          // Tetap lanjut tanpa data timbangan, gunakan target_bobot dari produk
        }

        // Format harga untuk display
        const formatPrice = (price) => {
          if (!price || price === 0) return '0';
          return new Intl.NumberFormat('id-ID').format(price);
        };

        // Mapping data dari API ke format yang digunakan di UI
        const formattedCow = {
          id: produkData.id || id,
          code: produkData.id || id,
          breed: produkData.jenis_produk_detail_nama || produkData.jenis_produk_nama || 'Sapi',
          name: produkData.nama || `Produk ${id}`,
          weight: bobotAktual,
          price: formatPrice(produkData.harga_jual || produkData.harga_beli),
          farm: produkData.kandang_nama || 'Kandang PakMoo',
          category: produkData.kategori_produk_nama || '',
          type: produkData.jenis_produk_detail_nama || produkData.jenis_produk_nama || '',
          position: produkData.kandang_nama || 'Kandang PakMoo',
          image: imageUrl,
          uniqueCode: `${produkData.id || id} ${new Date().getFullYear()}`,
          isSold: produkData.is_sold || false
        };

        setCow(formattedCow);
      } catch (err) {
        console.error('Error fetching cow data:', err);
        setError(err.message || 'Gagal memuat data produk');
      } finally {
        setLoading(false);
      }
    };

    fetchCowData();
  }, [id]);

  const handleContactClick = () => {
    window.open('https://wa.me/6281234567890', '_blank');
  };

  const goBack = () => {
    navigate('/katalog');
  };

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <div className="container mx-auto px-6 lg:px-12 py-6 pt-40 lg:pt-36">
          <button
            onClick={goBack}
            className="flex items-center space-x-2 text-primary hover:text-secondary transition-colors mb-6"
          >
            <i className="fa-solid fa-arrow-left"></i>
            <span>Kembali ke Katalog</span>
          </button>
          <div className="text-center py-12">
            <i className="fa-solid fa-exclamation-triangle text-red-500 text-6xl mb-4"></i>
            <p className="text-gray-600 text-lg">{error}</p>
            <button
              onClick={() => navigate('/katalog')}
              className="mt-6 bg-primary text-white py-2 px-6 rounded-lg hover:bg-secondary transition-colors"
            >
              Kembali ke Katalog
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Back Button */}
      <div className="container mx-auto px-6 lg:px-12 py-6 pt-40 lg:pt-36">
        <button
          onClick={goBack}
          className="flex items-center space-x-2 text-primary hover:text-secondary transition-colors"
        >
          <i className="fa-solid fa-arrow-left"></i>
          <span>Kembali ke Katalog</span>
        </button>
      </div>

      <div className="container mx-auto px-6 lg:px-12">
        {loading ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            {/* Loading Skeleton */}
            <div className="bg-gradient-to-br from-accent/30 to-accent/10 rounded-3xl p-8 animate-pulse">
              <div className="h-20 bg-gray-300 rounded-2xl mb-6"></div>
              <div className="h-96 bg-gray-300 rounded-2xl mb-6"></div>
            </div>
            <div className="bg-white p-8">
              <div className="h-8 bg-gray-300 rounded mb-4"></div>
              <div className="h-10 bg-gray-300 rounded w-1/2 mb-8"></div>
              <div className="space-y-4">
                <div className="h-4 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded"></div>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Left Section - Visual Area */}
          <div className="bg-gradient-to-br from-accent/30 to-accent/10 rounded-3xl p-8 relative overflow-hidden">
            {/* Background Wave Pattern */}
            <div className="absolute top-0 left-0 right-0">
              <svg viewBox="0 0 1200 120" className="w-full h-20 text-accent/20">
                <path d="M0,60 Q300,20 600,60 T1200,60 L1200,0 L0,0 Z" fill="currentColor"/>
              </svg>
            </div>
            <div className="absolute bottom-0 left-0 right-0">
              <svg viewBox="0 0 1200 120" className="w-full h-20 text-accent/20">
                <path d="M0,0 Q300,40 600,0 T1200,0 L1200,120 L0,120 Z" fill="currentColor"/>
              </svg>
            </div>

            {/* Product ID Banner */}
            <div className="relative z-10 mb-6">
              <div className="bg-primary rounded-2xl px-6 py-4 inline-block">
                <div className="text-white text-3xl font-bold">{cow.code}</div>
                <div className="text-white text-lg">{cow.breed}</div>
              </div>
            </div>

            {/* Main Cow Image */}
            <div className="relative z-10 mb-6">
              <img
                src={cow.image}
                alt={cow.name}
                className="w-full h-96 object-cover rounded-2xl shadow-2xl"
              />
            </div>

            {/* Hashtags */}
            <div className="relative z-10 mb-8">
              <div className="flex items-center space-x-4 text-white text-xl font-semibold">
                <span className="text-3xl">#</span>
                <span>IngatQurban</span>
              </div>
              <div className="flex items-center space-x-4 text-white text-xl font-semibold mt-2">
                <span className="text-3xl">#</span>
                <span>IngatPakMoo</span>
              </div>
            </div>

            {/* Bottom Logos */}
            <div className="relative z-10 flex justify-between items-end">
              <div className="text-center">
                <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 inline-block">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-2">
                    <i className="fa-solid fa-cow text-primary text-2xl"></i>
                  </div>
                  <div className="text-white text-sm font-semibold">PAK MOO</div>
                  <div className="text-white text-sm font-semibold">FARM</div>
                </div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-primary">Pak Moo</div>
                <div className="text-sm text-primary/80">Qurban Solution</div>
              </div>
            </div>

            {/* Decorative Elements */}
            <div className="absolute bottom-4 left-4 w-8 h-8 bg-green-600 rounded-full opacity-30"></div>
            <div className="absolute bottom-8 right-8 w-6 h-6 bg-green-600 rounded-full opacity-30"></div>
          </div>

          {/* Right Section - Information Area */}
          <div className="bg-white p-8">
            {/* Product Title */}
            <h1 className="text-3xl font-bold text-primary mb-4">{cow.name}</h1>
            
            {/* Price */}
            <div className="text-3xl font-bold text-red-600 mb-8">
              Rp {cow.price}
            </div>

            {/* Detail Information */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-primary mb-4">Detail Informasi :</h2>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-center">
                  <i className="fa-solid fa-barcode text-accent mr-3"></i>
                  <span><strong>Kode Unik:</strong> {cow.uniqueCode}</span>
                </li>
                <li className="flex items-center">
                  <i className="fa-solid fa-star text-accent mr-3"></i>
                  <span><strong>Kategori:</strong> {cow.category}</span>
                </li>
                <li className="flex items-center">
                  <i className="fa-solid fa-cow text-accent mr-3"></i>
                  <span><strong>Jenis:</strong> {cow.type}</span>
                </li>
                <li className="flex items-center">
                  <i className="fa-solid fa-weight-scale text-accent mr-3"></i>
                  <span><strong>Bobot:</strong> {cow.weight}</span>
                </li>
                <li className="flex items-center">
                  <i className="fa-solid fa-map-marker-alt text-accent mr-3"></i>
                  <span><strong>Posisi:</strong> {cow.position}</span>
                </li>
                <li className="flex items-center">
                  <i className={`fa-solid ${cow.isSold ? 'fa-check-circle' : 'fa-times-circle'} text-accent mr-3`}></i>
                  <span><strong>Status:</strong> <span className={cow.isSold ? 'text-red-600 font-semibold' : 'text-green-600 font-semibold'}>{cow.isSold ? 'Terjual' : 'Tersedia'}</span></span>
                </li>
              </ul>
            </div>

            {/* Video Links */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-primary mb-4">
                Lihat Video Hewan Qurban Via Telegram
              </h2>
              <ul className="space-y-2">
                <li>
                  <a 
                    href="https://t.me/solusiqurban" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline flex items-center"
                  >
                    <i className="fa-solid fa-video text-accent mr-3"></i>
                    Video Sapi: t.me/solusiqurban
                  </a>
                </li>
                <li>
                  <a 
                    href="https://t.me/solusiqurbanpakmoo" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline flex items-center"
                  >
                    <i className="fa-solid fa-video text-accent mr-3"></i>
                    Video Kambing & Domba: t.me/solusiqurbanpakmoo
                  </a>
                </li>
              </ul>
            </div>

            {/* Benefits Section */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-primary mb-4">
                Mengapa berqurban bersama Pak Moo Farm melalui program Solusi Qurban?
              </h2>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <i className="fa-solid fa-shield-check text-green-600 mr-3 mt-1"></i>
                  <span>Garansi jika sakit/mati/cacat</span>
                </li>
                <li className="flex items-start">
                  <i className="fa-solid fa-tag text-green-600 mr-3 mt-1"></i>
                  <span>Harga pasti lebih murah</span>
                </li>
                <li className="flex items-start">
                  <i className="fa-solid fa-truck text-green-600 mr-3 mt-1"></i>
                  <span>Free ongkir Jabodetabek, free biaya perawatan dan sertifikat</span>
                </li>
                <li className="flex items-start">
                  <i className="fa-solid fa-gift text-green-600 mr-3 mt-1"></i>
                  <span>Berhadiah Umroh</span>
                </li>
                <li className="flex items-start">
                  <i className="fa-solid fa-weight text-green-600 mr-3 mt-1"></i>
                  <span>Garansi bobot timbang real</span>
                </li>
                <li className="flex items-start">
                  <i className="fa-solid fa-heart text-green-600 mr-3 mt-1"></i>
                  <span>Hewan Qurban rawatan lebih berkualitas, layak dan sehat</span>
                </li>
              </ul>
            </div>

            {/* Thank You Message */}
            <div className="text-center mb-8">
              <p className="text-xl font-semibold text-primary">Terima Kasih !!</p>
            </div>

            {/* Contact Button */}
            <button
              onClick={handleContactClick}
              className="w-full bg-primary text-white py-4 px-6 rounded-2xl font-semibold text-lg hover:bg-secondary transition-colors duration-300 flex items-center justify-center space-x-3 shadow-lg hover:shadow-xl"
            >
              <i className="fa-brands fa-whatsapp text-2xl"></i>
              <span>Hubungi Kami</span>
            </button>
          </div>
        </div>
          )}
      </div>
    </div>
  );
};

export default CowDetail;
